import { apiBaseUrl } from '../config';

/**
 * The one way this front end talks to the API.
 *
 * It sets the base URL, attaches a bearer token it is handed, and turns every
 * failure into an `ApiError`. It does not store, refresh or choose where a
 * token lives: that is the session (F4), and how it is held is X1.
 *
 * The error body is read off `veri-plus-backend/src/middlewares/errorHandler.ts`
 * at commit `042f3b7`: `{ code, message, details? }`, with `Retry-After` on a
 * rate limit. See D5.
 */

/** What a screen branches on. `network` means no HTTP response arrived at all. */
export type ApiErrorKind =
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'invalid'
  | 'rate_limited'
  | 'server'
  | 'network'
  | 'unexpected';

export class ApiError extends Error {
  readonly status: number;
  readonly kind: ApiErrorKind;
  /** The API's machine code, for example `missing_token` or `validation_failed`. */
  readonly code: string;
  /** Field-level messages keyed by path, for example `body.email`. */
  readonly details?: Record<string, string>;
  readonly retryAfterSeconds?: number;

  constructor(
    status: number,
    code: string,
    message: string,
    extra: { details?: Record<string, string>; retryAfterSeconds?: number } = {},
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.kind = kindOf(status);
    this.code = code;
    if (extra.details !== undefined) this.details = extra.details;
    if (extra.retryAfterSeconds !== undefined) this.retryAfterSeconds = extra.retryAfterSeconds;
  }
}

export function kindOf(status: number): ApiErrorKind {
  if (status === 0) return 'network';
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 400 || status === 409 || status === 422) return 'invalid';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'server';
  return 'unexpected';
}

export interface ApiClientOptions {
  baseUrl?: string;
  /** Supplied by the session. Returns the current access token, or null when signed out. */
  getToken?: () => string | null;
  fetch?: typeof fetch;
}

export interface RequestOptions {
  /** Sent as JSON, except `FormData`, which is sent as multipart untouched. */
  body?: unknown;
  signal?: AbortSignal;
  /** Resolve with the body as a `Blob` rather than parsed JSON. Errors are still the JSON envelope. */
  blob?: boolean;
  /** Extra request headers, for example a step-up's `X-Otp-Challenge` and `X-Otp-Code`. */
  headers?: Record<string, string>;
}

export type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE';

export interface ApiClient {
  request<T>(method: Method, path: string, options?: RequestOptions): Promise<T>;
  get<T>(path: string, options?: RequestOptions): Promise<T>;
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>;
  delete<T>(path: string, options?: RequestOptions): Promise<T>;
}

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const baseUrl = options.baseUrl ?? apiBaseUrl;
  const getToken = options.getToken ?? (() => null);
  // Read at call time, not captured, so a test or a polyfill that replaces the
  // global after import is still the one used.
  const doFetch = (input: string, init: RequestInit) => (options.fetch ?? fetch)(input, init);

  async function request<T>(method: Method, path: string, opts: RequestOptions = {}): Promise<T> {
    if (!path.startsWith('/')) {
      throw new Error(`API paths start with a slash: ${path}`);
    }

    const headers: Record<string, string> = { ...opts.headers, Accept: 'application/json' };
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;

    const init: RequestInit = { method, headers };
    if (opts.body instanceof FormData) {
      // No Content-Type: fetch writes it, with the boundary the parts need.
      init.body = opts.body;
    } else if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(opts.body);
    }
    if (opts.signal) init.signal = opts.signal;

    let res: Response;
    try {
      res = await doFetch(baseUrl + path, init);
    } catch (err) {
      // An abort is the caller's decision, not a failure to report.
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      // A refused CORS preflight lands here too: the browser hides the response.
      throw new ApiError(
        0,
        'network_error',
        err instanceof Error ? err.message : 'Network request failed.',
      );
    }

    if (opts.blob && res.ok) return (await res.blob()) as T;

    const text = await res.text();
    const parsed = parseJson(text);

    if (!res.ok) throw toApiError(res, parsed);
    return (text === '' ? undefined : parsed) as T;
  }

  return {
    request,
    get: (path, opts) => request('GET', path, opts),
    post: (path, body, opts) => request('POST', path, { ...opts, body }),
    patch: (path, body, opts) => request('PATCH', path, { ...opts, body }),
    delete: (path, opts) => request('DELETE', path, opts),
  };
}

function parseJson(text: string): unknown {
  if (text === '') return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

function toApiError(res: Response, body: unknown): ApiError {
  const retryHeader = res.headers.get('Retry-After');
  const retryAfterSeconds =
    retryHeader !== null && /^\d+$/.test(retryHeader) ? Number(retryHeader) : undefined;
  const extra: { details?: Record<string, string>; retryAfterSeconds?: number } = {};
  if (retryAfterSeconds !== undefined) extra.retryAfterSeconds = retryAfterSeconds;

  if (isErrorBody(body)) {
    if (isStringRecord(body.details)) extra.details = body.details;
    return new ApiError(res.status, body.code, body.message, extra);
  }
  // Not the API's envelope: a proxy page, an empty 502, a gateway timeout.
  return new ApiError(
    res.status,
    'unrecognised_response',
    res.statusText || `HTTP ${res.status}`,
    extra,
  );
}

function isErrorBody(body: unknown): body is { code: string; message: string; details?: unknown } {
  return (
    typeof body === 'object' &&
    body !== null &&
    typeof (body as { code?: unknown }).code === 'string' &&
    typeof (body as { message?: unknown }).message === 'string'
  );
}

function isStringRecord(value: unknown): value is Record<string, string> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every((v) => typeof v === 'string')
  );
}
